import React, { useEffect, useState } from "react";
import { Save, Settings as SettingsIcon, Mic, Speaker, Clock } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";

export default function Settings() {
  const { t } = useI18n();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { (async () => { const r = await api.get("/settings"); setS(r.data); })(); }, []);

  if (!s) return <div className="text-slate-500">{t("common.loading")}</div>;

  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });
  const setNum = (k) => (e) => setS({ ...s, [k]: parseInt(e.target.value || "0", 10) });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setSaved(false);
    const { tenant_id, updated_at, ...payload } = s;
    const r = await api.put("/settings", payload);
    setS(r.data); setBusy(false); setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const Section = ({ icon: Icon, title, children }) => (
    <div className="glass rounded-2xl p-6">
      <div className="flex items-center gap-3 pb-4 border-b border-white/5">
        <div className="h-9 w-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center"><Icon className="h-4 w-4 text-magenta" /></div>
        <h3 className="text-base font-bold text-white">{title}</h3>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
    </div>
  );

  const Field = ({ label, children, full }) => (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="text-xs uppercase tracking-widest text-slate-400 font-bold">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="settings-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="chip"><SettingsIcon className="h-3 w-3" /> {t("settings.title")}</div>
          <h1 className="mt-3 text-3xl lg:text-4xl font-extrabold text-white tracking-tight">{t("settings.title")}</h1>
          <p className="mt-1 text-slate-400 text-sm">{t("settings.sub")}</p>
        </div>
        <div className="flex items-center gap-3">
          {saved && <span className="text-emerald-400 text-sm font-semibold" data-testid="settings-saved">{t("settings.saved")}</span>}
          <button type="submit" disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="settings-save"><Save className="h-4 w-4" /> {t("settings.save")}</button>
        </div>
      </div>

      <Section icon={SettingsIcon} title={t("settings.title")}>
        <Field label={t("settings.persona")} full>
          <textarea rows={2} value={s.persona} onChange={set("persona")} className="input-dark resize-none" data-testid="set-persona" />
        </Field>
        <Field label={t("settings.greeting")} full>
          <input value={s.greeting_message} onChange={set("greeting_message")} className="input-dark" data-testid="set-greeting" />
        </Field>
        <Field label={t("settings.primary_lang")}>
          <select value={s.primary_language} onChange={set("primary_language")} className="input-dark" data-testid="set-primary-lang">
            <option value="mn">Монгол (mn)</option>
            <option value="en">English (en)</option>
          </select>
        </Field>
        <Field label={t("settings.fallback_lang")}>
          <select value={s.fallback_language} onChange={set("fallback_language")} className="input-dark" data-testid="set-fallback-lang">
            <option value="en">English (en)</option>
            <option value="mn">Монгол (mn)</option>
            <option value="none">— none —</option>
          </select>
        </Field>
        <Field label={t("settings.max_turns")}>
          <input type="number" min={1} max={50} value={s.max_turns} onChange={setNum("max_turns")} className="input-dark" data-testid="set-max-turns" />
        </Field>
      </Section>

      <Section icon={Clock} title={t("settings.hours")}>
        <Field label={t("settings.hours")}>
          <div className="flex gap-2">
            <input value={s.business_hours_start} onChange={set("business_hours_start")} className="input-dark font-mono" placeholder="09:00" data-testid="set-hours-start" />
            <input value={s.business_hours_end} onChange={set("business_hours_end")} className="input-dark font-mono" placeholder="18:00" data-testid="set-hours-end" />
          </div>
        </Field>
        <Field label={t("settings.tz")}>
          <input value={s.timezone} onChange={set("timezone")} className="input-dark" data-testid="set-tz" />
        </Field>
        <Field label={t("settings.after_hours")} full>
          <textarea rows={2} value={s.after_hours_message} onChange={set("after_hours_message")} className="input-dark resize-none" data-testid="set-after-hours" />
        </Field>
      </Section>

      <Section icon={Mic} title={t("settings.stt_section")}>
        <Field label={t("settings.stt_url")}>
          <input value={s.stt_provider_url} onChange={set("stt_provider_url")} className="input-dark font-mono" placeholder="https://your-stt.example.com/api" data-testid="set-stt-url" />
        </Field>
        <Field label={t("settings.stt_lang")}>
          <input value={s.stt_language_code} onChange={set("stt_language_code")} className="input-dark font-mono" placeholder="mn-MN" data-testid="set-stt-lang" />
        </Field>
        <Field label={t("settings.stt_hotwords")} full>
          <input value={s.stt_hotwords || ""} onChange={set("stt_hotwords")} className="input-dark font-mono" placeholder="пицца, пепперони, маргарита" data-testid="set-stt-hotwords" />
          <div className="mt-1 text-xs text-slate-500">{t("settings.stt_hotwords_help")}</div>
        </Field>
        <Field label={t("settings.stt_key")} full>
          <input type="password" value={s.stt_api_key} onChange={set("stt_api_key")} className="input-dark font-mono" data-testid="set-stt-key" />
        </Field>
      </Section>

      <Section icon={Speaker} title={t("settings.tts_section")}>
        <Field label={t("settings.tts_provider")}>
          <select value={s.tts_provider || "azure"} onChange={set("tts_provider")} className="input-dark" data-testid="set-tts-provider">
            <option value="azure">Microsoft Azure</option>
            <option value="elevenlabs">ElevenLabs</option>
            <option value="custom">Custom (URL)</option>
          </select>
        </Field>
        {(!s.tts_provider || s.tts_provider === "azure") && (
          <>
            <Field label={t("settings.tts_region")}>
              <input value={s.tts_azure_region || ""} onChange={set("tts_azure_region")} className="input-dark font-mono" placeholder="eastus" data-testid="set-tts-region" />
            </Field>
            <Field label={t("settings.tts_voice")}>
              <select value={s.tts_voice_id || "yesui"} onChange={set("tts_voice_id")} className="input-dark" data-testid="set-tts-voice">
                <option value="yesui">mn-MN Yesui (female)</option>
                <option value="bataa">mn-MN Bataa (male)</option>
              </select>
            </Field>
          </>
        )}
        {s.tts_provider === "elevenlabs" && (
          <Field label={t("settings.tts_voice")} full>
            <div className="text-xs text-slate-500">
              ElevenLabs is configured by the platform (.env). Tenants don’t need to set voice/model keys here.
            </div>
          </Field>
        )}
        <Field label={t("settings.tts_url")}>
          <input value={s.tts_provider_url} onChange={set("tts_provider_url")} className="input-dark font-mono" placeholder="https://your-tts.example.com/api" data-testid="set-tts-url" />
        </Field>
        {(!s.tts_provider || s.tts_provider === "azure") && (
          <Field label={t("settings.tts_key")} full>
            <input type="password" value={s.tts_api_key} onChange={set("tts_api_key")} className="input-dark font-mono" data-testid="set-tts-key" />
          </Field>
        )}
      </Section>
    </form>
  );
}
